import React, { useState, useEffect } from 'react';
import {
  Paper,
  Box,
  TextField,
  List,
  ListItem,
  ListItemButton,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Badge,
  Typography,
  CircularProgress,
  Alert,
  AppBar,
  Toolbar,
  IconButton,
  Button,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SearchIcon from '@mui/icons-material/Search';
import LockClockIcon from '@mui/icons-material/LockClock';
import { useNavigate } from 'react-router-dom';
import { getConversations, ConversationResponse } from '../api/messagesApi';
import EmptyState from './EmptyState';
import { formatTimestampKST } from '../../../utils/dateUtils';

interface MessageListPanelProps {
  selectedId: string | null;
  onSelect: (id: string) => void;
}

export default function MessageListPanel({ selectedId, onSelect }: MessageListPanelProps) {
  const navigate = useNavigate();
  const [conversations, setConversations] = useState<ConversationResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadConversations();
  }, []);

  // Short polling: 10초마다 대화 목록 갱신
  const LIST_POLL_INTERVAL = 10000;
  useEffect(() => {
    const poll = setInterval(async () => {
      if (document.hidden) return;
      try {
        const response = await getConversations();
        setConversations(response?.content || []);
      } catch {
        // 에러 시 다음 interval에 재시도
      }
    }, LIST_POLL_INTERVAL);

    return () => clearInterval(poll);
  }, []);

  const loadConversations = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await getConversations();
      setConversations(response?.content || []);
    } catch (err: any) {
      setError(err.message || '대화 목록을 불러올 수 없습니다.');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Paper elevation={1} sx={{ p: 4, textAlign: 'center' }}>
        <CircularProgress />
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          대화 목록을 불러오는 중...
        </Typography>
      </Paper>
    );
  }

  if (error) {
    return (
      <Paper elevation={1} sx={{ p: 2 }}>
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      </Paper>
    );
  }

  return (
    <Paper elevation={1} sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <AppBar
        position="static"
        elevation={0}
        sx={{
          bgcolor: 'white',
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        <Toolbar sx={{ minHeight: 56 }}>
          <IconButton
            onClick={() => navigate('/')}
            sx={{ mr: 1, minWidth: 44, minHeight: 44 }}
            aria-label="대시보드로 돌아가기"
          >
            <ArrowBackIcon sx={{ color: 'text.primary' }} />
          </IconButton>
          <Typography
            variant="h6"
            sx={{ fontWeight: 600, color: 'text.primary', flexGrow: 1 }}
          >
            내 쪽지함
          </Typography>
        </Toolbar>
      </AppBar>
      <Box p={2} borderBottom={1} borderColor="divider">
        <TextField
          fullWidth
          size="small"
          placeholder="대화 검색..."
          InputProps={{
            startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
          }}
        />
      </Box>

      <List sx={{ flex: 1, overflow: 'auto' }}>
        {conversations.length === 0 ? (
          <EmptyState />
        ) : (
          conversations.map((conv) => {
            const isSelected = selectedId === String(conv.id);
            return (
              <ListItem key={conv.id} disablePadding>
                <ListItemButton
                  selected={isSelected}
                  onClick={() => onSelect(String(conv.id))}
                  sx={{
                    py: 3,
                    px: 2.5,
                    minHeight: 96,
                    borderBottom: 1,
                    borderColor: 'divider',
                    bgcolor: isSelected ? 'rgba(13,31,60,0.06)' : 'white',
                    '&:hover': {
                      bgcolor: isSelected ? 'rgba(13,31,60,0.06)' : 'grey.50',
                    },
                    opacity: conv.isExpired && conv.isExtendable ? 0.75 : conv.isExpired && !conv.isExtendable ? 0.5 : 1,
                  }}
                >
                  <ListItemAvatar>
                    <Badge
                      badgeContent={conv.unreadCount}
                      color="error"
                      invisible={conv.unreadCount === 0}
                      max={99}
                    >
                      <Avatar
                        src={conv.otherUserProfileImageUrl || undefined}
                        sx={{
                          width: 56,
                          height: 56,
                          bgcolor: conv.isExpired ? 'grey.400' : '#E8CFA0',
                          fontSize: '1.5rem',
                          fontWeight: 600,
                          filter: conv.isExpired && conv.isExtendable
                            ? 'grayscale(60%)'
                            : conv.isExpired && !conv.isExtendable
                            ? 'grayscale(100%)'
                            : 'none',
                        }}
                      >
                        {conv.otherUserNickname?.[0] || '?'}
                      </Avatar>
                    </Badge>
                  </ListItemAvatar>

                  <ListItemText
                    sx={{ ml: 2 }}
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5 }}>
                        <Typography
                          variant="subtitle1"
                          sx={{
                            fontSize: '1rem',
                            fontWeight: 600,
                            color: conv.isExpired ? 'text.disabled' : 'text.primary',
                          }}
                        >
                          {conv.otherUserNickname}
                        </Typography>
                        {conv.isExpired && conv.isExtendable && (
                          <Box
                            sx={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: 0.25,
                              px: 0.75,
                              py: 0.25,
                              bgcolor: '#FFF5F0',
                              borderRadius: 1,
                              flexShrink: 0,
                            }}
                          >
                            <LockClockIcon sx={{ fontSize: 11, color: '#FF6B35' }} />
                            <Typography sx={{ fontSize: '0.6875rem', fontWeight: 700, color: '#FF6B35', lineHeight: 1 }}>
                              ⚠️ 대화 만료
                            </Typography>
                          </Box>
                        )}
                        {conv.isExpired && !conv.isExtendable && (
                          <Box
                            sx={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: 0.25,
                              px: 0.75,
                              py: 0.25,
                              bgcolor: '#F0F3F8',
                              borderRadius: 1,
                              flexShrink: 0,
                            }}
                          >
                            <LockClockIcon sx={{ fontSize: 11, color: '#8896AB' }} />
                            <Typography sx={{ fontSize: '0.6875rem', fontWeight: 700, color: '#8896AB', lineHeight: 1 }}>
                              🔒 대화 종료
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    }
                    secondary={
                      <Typography
                        variant="body2"
                        component="span"
                        sx={{
                          fontSize: '0.875rem',
                          color: conv.isExpired ? 'text.disabled' : 'text.secondary',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          display: 'block',
                          fontStyle: conv.isExpired && conv.isExtendable ? 'italic' : 'normal',
                        }}
                      >
                        {conv.isExpired && !conv.isExtendable
                          ? '───'
                          : conv.isExpired && conv.isExtendable && !conv.lastMessage
                          ? '대화 기간이 종료되었어요'
                          : conv.lastMessage || '메시지 없음'}
                      </Typography>
                    }
                  />

                  {conv.isExpired && conv.isExtendable && (
                    <Box sx={{ ml: 1, flexShrink: 0 }} onClick={(e) => e.stopPropagation()}>
                      <Button
                        variant="outlined"
                        size="small"
                        sx={{
                          height: 32,
                          minWidth: 'auto',
                          px: 1,
                          fontSize: '0.75rem',
                          fontWeight: 700,
                          color: '#FF6B35',
                          borderColor: '#FF6B35',
                          '&:hover': { bgcolor: '#FFF3E0', borderColor: '#FF6B35' },
                          whiteSpace: 'nowrap',
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          onSelect(String(conv.id));
                        }}
                      >
                        연장 ♥3
                      </Button>
                    </Box>
                  )}

                  <Box sx={{ ml: 'auto', textAlign: 'right' }}>
                    <Typography
                      variant="caption"
                      sx={{
                        fontSize: '0.75rem',
                        color: 'text.secondary',
                      }}
                    >
                      {formatTimestampKST(conv.lastMessageAt)}
                    </Typography>
                  </Box>
                </ListItemButton>
              </ListItem>
            );
          })
        )}
      </List>
    </Paper>
  );
}
